import { IModel } from '../base/annotation';
import { Metamodel } from '../base/model';
import { Column } from './column';
import * as jsonpath from '../../../../../../External/jsonpath';
import { buildJsonPath } from '../../shared/utils/jsonpath.util';
import { stringIsNullOrEmpty, parseJsonPath } from '../../shared/utils/string.util';

interface IJSONPathItem {
    name: string;
    path: string;
    // eslint-disable-next-line
    type: string;
}

@Metamodel.model({
    name: 'heirarchicalnode',
    referable: true,
    globalScope: true
})
export class HierarchicalNode extends Column {
    public static readonly MODEL: IModel;

    // when type is object, properties describes the object items
    public properties: { [key: string]: HierarchicalNode };
    // when type is array, the item describes the array item structure
    public items: HierarchicalNode;

    public selectable?: boolean;

    public kind: 'array' | 'object' | 'primitive';

    public get isObject(): boolean {
        return /object/i.test(this.columnType) || this.kind === 'object';
    }

    public get isArray(): boolean {
        return /array/i.test(this.columnType) || this.kind === 'array';
    }

    public get children(): HierarchicalNode[] {
        if (this.isObject) {
            return this.properties != null ? Object.keys(this.properties).map((key) => this.properties[key]) : [];
        } else if (this.isArray) {
            return this.items != null ? [this.items] : [];
        }
        return [];
    }

    constructor(name?: string, description?: string) {
        super(name, description);
    }

    public static enumerateJSONPath(node: HierarchicalNode, isRoot?: boolean): IJSONPathItem[] {
        const pathItemStack: jsonpath.PathComponent[] = [];
        const result: IJSONPathItem[] = [];
        // recursively get all leaf node json path
        const enumerateItem = (n: HierarchicalNode, root?: boolean) => {
            if (root) {
                pathItemStack.push('$');
            } else {
                pathItemStack.push(n.name);
            }
            // count the number array, ignore items and push '[0]' to the end of path for each array
            let arrayCount = 0;
            while (n.isArray) {
                n = n.items;
                pathItemStack.push(0);
                arrayCount++;
            }
            // navigate into properties for object
            if (n.children.length > 0) {
                n.children.forEach((child) => enumerateItem(child));
            } else {
                // if there is no inner property, emit the result
                result.push({
                    path: buildJsonPath(pathItemStack, true),
                    name: n.name,
                    type: n.columnType
                });
            }
            // pop all array after emit
            pathItemStack.pop();
            for (let i = 0; i < arrayCount; ++i) {
                pathItemStack.pop();
            }
        };
        enumerateItem(node, isRoot);
        return result;
    }

    /**
     * Deep clone an hierarchical node
     * @param node the node to be cloned
     * @param typeFilter only child node with specified type will be cloned
     */
    public static clone(node: HierarchicalNode, typeFilter?: string): HierarchicalNode {
        const cloneInternal = (n: HierarchicalNode): { cloned: HierarchicalNode, containsTypeFilter?: boolean } => {
            const cloned = new HierarchicalNode(n.name, n.description);
            cloned.columnType = n.columnType;
            if (typeFilter != null && cloned.columnType != null && cloned.columnType.toLowerCase() === typeFilter.toLowerCase()) {
                // found the type, cut all children items
                return {
                    cloned,
                    containsTypeFilter: true
                };
            }
            let hasTypeFilter = false;
            if (n.properties != null) {
                cloned.properties = {};
                Object.keys(n.properties).forEach((key) => {
                    const ans = cloneInternal(n.properties[key]);
                    if (typeFilter == null || ans.containsTypeFilter) {
                        cloned.properties[key] = ans.cloned;
                        hasTypeFilter = true;
                    }
                });
            }
            if (n.items != null) {
                const ans = cloneInternal(n.items);
                if (typeFilter == null || ans.containsTypeFilter) {
                    cloned.items = ans.cloned;
                    hasTypeFilter = true;
                }
            }
            return {
                cloned,
                containsTypeFilter: hasTypeFilter
            };
        };
        return cloneInternal(node).cloned;
    }

    /**
     * Find the specified node by using JSON path.
     * Example: $.abc.prop1[0], the result will we currentNode -> child node 'abc' -> items of the child node 'prop1'
     * @param rootPath The json path of the node to be searched
     */
    public findNodeByPath(rootPath: string): HierarchicalNode {
        const pathItem = parseJsonPath(rootPath);
        if (pathItem != null) {
            let curNode: HierarchicalNode = this;
            if (pathItem[0] === '$') {
                pathItem.shift(); // remove root $
            }
            while (pathItem.length > 0) {
                if (curNode == null) {
                    // Not found
                    return undefined;
                }
                const curItem = pathItem.shift();
                if (typeof curItem === 'number') {
                    curNode = curNode.items;
                } else {
                    if (curNode.properties == null) {
                        // Not found
                        return undefined;
                    }
                    curNode = curNode.properties[curItem];
                }
            }
            return curNode;
        }
        return undefined;
    }

    public insertNodeByPath(path: string): void {
        const pathItem = parseJsonPath(path);
        if (pathItem != null) {
            let curNode: HierarchicalNode = this;
            while (pathItem.length > 0) {
                const curItem = pathItem.shift();
                if (curItem === '$') {
                    continue;
                }
                if (typeof curItem === 'number') {
                    if (!stringIsNullOrEmpty(curNode.columnType) && !curNode.isArray) {
                        throw new Error('Json path (' + path + ') has conflict with current schema.');
                    }
                    curNode.kind = 'array';
                    if (curNode.items == null) {
                        curNode.items = new HierarchicalNode('items');
                    }
                    curNode = curNode.items;
                } else {
                    if (!stringIsNullOrEmpty(curNode.columnType) && !curNode.isObject) {
                        throw new Error('Json path (' + path + ') has conflict with current schema.');
                    }
                    curNode.kind = 'object';
                    if (curNode.properties == null) {
                        curNode.properties = {};
                    }
                    if (curNode.properties[curItem] == null) {
                        curNode.properties[curItem] = new HierarchicalNode(curItem);
                    }
                    curNode = curNode.properties[curItem];
                }
            }
        }
    }
}
